import React, {
     useCallback,
     useContext,
     useEffect,
     useMemo,
     useState,
} from "react";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Input } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import Tooltip from "@mui/material/Tooltip";
import { useDispatch, useSelector } from "react-redux";

import {
     retailSalesClearSaveJobTitlesResult,
     retailSalesDeleteJobTitleRequest,
     retailSalesGetJobTitlesRequest,
     retailSalesSaveJobTitlesRequest,
} from "../../../../../_redux/_actions";
import { contextApiActions } from "../../../../../_redux/_constants/common.constants";
import { retailSalesConstants } from "../../../../../_redux/_constants/retailSales.constants";
import { Context } from "../../../../../_context/Store";

/**
 * The job titles a retail sales winner can be labelled with. This list is only ever a label: it is
 * kept apart from the participant job roles, so adding a title here never grants a portal view.
 */
const RetailJobTitlesGrid = () => {
     const actionDispatch = useDispatch();
     const [, contextDispatch] = useContext(Context);

     const jobTitles = useSelector((state) => state?.retailSales?.jobTitleList);
     const saveResult = useSelector(
          (state) => state?.retailSales?.saveJobTitlesResult,
     );
     const isLoading = useSelector((state) =>
          (state?.loading?.pendingRequests || []).includes(
               retailSalesConstants.RETAIL_SALES_GET_JOB_TITLES_REQUEST,
          ),
     );

     const [search, setSearch] = useState("");
     const [newTitle, setNewTitle] = useState("");
     const [editing, setEditing] = useState({});
     const [failureMessage, setFailureMessage] = useState("");

     // The search is applied by the backend, so the list is re-read rather than narrowed here.
     const loadJobTitles = useCallback(() => {
          actionDispatch(
               retailSalesGetJobTitlesRequest({
                    search: search.trim() ? search.trim() : undefined,
               }),
          );
     }, [actionDispatch, search]);

     useEffect(() => {
          const timer = setTimeout(loadJobTitles, 300);

          return () => clearTimeout(timer);
     }, [loadJobTitles]);

     useEffect(() => {
          if (saveResult) {
               const failed = (saveResult.results || []).find(
                    (result) => !result.isSuccess,
               );

               setFailureMessage(failed ? failed.message : "");

               if (!failed) {
                    setNewTitle("");
                    setEditing({});
               }

               actionDispatch(retailSalesClearSaveJobTitlesResult());
               loadJobTitles();
          }
     }, [actionDispatch, saveResult, loadJobTitles]);

     const save = useCallback(
          (jobTitle) => {
               actionDispatch(
                    retailSalesSaveJobTitlesRequest({ jobTitles: [jobTitle] }),
               );
          },
          [actionDispatch],
     );

     // Deleting removes the row outright. A winner's job title is kept as text on their own record,
     // so nothing already recorded changes.
     const confirmDelete = useCallback(
          (jobTitle) => {
               contextDispatch({
                    type: contextApiActions.UI_CONFIRMATION_TOGGEL,
                    payload: {
                         show: true,
                         title: "Delete",
                         contentDetails: `Delete "${jobTitle.name}" from the job title list?`,
                         okFunction: () =>
                              actionDispatch(
                                   retailSalesDeleteJobTitleRequest({
                                        id: jobTitle.id,
                                   }),
                              ),
                    },
               });
          },
          [actionDispatch, contextDispatch],
     );

     const rows = useMemo(
          () =>
               (Array.isArray(jobTitles) ? jobTitles : []).map((jobTitle) => ({
                    ...jobTitle,
                    editedName:
                         editing[jobTitle.id] !== undefined
                              ? editing[jobTitle.id]
                              : jobTitle.name,
               })),
          [jobTitles, editing],
     );

     const cells = useMemo(
          () => ({
               name: (cellProps) => (
                    <td>
                         <Input
                              style={{ width: "100%" }}
                              value={cellProps.dataItem.editedName}
                              onChange={(event) =>
                                   setEditing((previous) => ({
                                        ...previous,
                                        [cellProps.dataItem.id]: event.value,
                                   }))
                              }
                         />
                    </td>
               ),

               active: (cellProps) => (
                    <td>
                         <input
                              type="checkbox"
                              className="k-checkbox k-checkbox-md"
                              checked={cellProps.dataItem.active}
                              onChange={(event) =>
                                   save({
                                        id: cellProps.dataItem.id,
                                        name: cellProps.dataItem.editedName,
                                        active: event.target.checked,
                                   })
                              }
                         />
                    </td>
               ),

               remove: (cellProps) => (
                    <td>
                         <Tooltip title="Delete" placement="top" arrow>
                              <IconButton
                                   aria-label="Delete"
                                   onClick={() => confirmDelete(cellProps.dataItem)}
                              >
                                   <DeleteIcon color="error" />
                              </IconButton>
                         </Tooltip>
                    </td>
               ),

               save: (cellProps) => (
                    <td>
                         <Button
                              type="button"
                              disabled={
                                   cellProps.dataItem.editedName ===
                                        cellProps.dataItem.name ||
                                   !cellProps.dataItem.editedName.trim()
                              }
                              onClick={() =>
                                   save({
                                        id: cellProps.dataItem.id,
                                        name: cellProps.dataItem.editedName,
                                        active: cellProps.dataItem.active,
                                   })
                              }
                         >
                              Save
                         </Button>
                    </td>
               ),
          }),
          [save, confirmDelete],
     );

     return (
          <div className="sba-telerik-components-wr">
               <div
                    className="sba-chart-section-top-search"
                    style={{
                         display: "flex",
                         flexDirection: "row",
                         flexWrap: "wrap",
                         alignItems: "flex-end",
                         gap: "12px",
                         width: "100%",
                    }}
               >
                    <Input
                         style={{ width: "280px" }}
                         value={search}
                         onChange={(event) => setSearch(event.value)}
                         placeholder="Search job title"
                    />

                    <div
                         style={{
                              marginLeft: "auto",
                              display: "flex",
                              gap: "8px",
                         }}
                    >
                         <Input
                              style={{ width: "260px" }}
                              value={newTitle}
                              onChange={(event) => setNewTitle(event.value)}
                              placeholder="New job title"
                         />
                         <Button
                              type="button"
                              themeColor="primary"
                              disabled={!newTitle.trim()}
                              onClick={() =>
                                   save({ name: newTitle.trim(), active: true })
                              }
                         >
                              Add
                         </Button>
                    </div>
               </div>

               {failureMessage && (
                    <p style={{ color: "#c62828", marginTop: "8px" }}>
                         {failureMessage}
                    </p>
               )}

               <div style={{ position: "relative" }}>
                    {isLoading && (
                         <div className="k-loading-mask" style={{ zIndex: 10 }}>
                              <span className="k-loading-text">Loading</span>
                              <div className="k-loading-image" />
                              <div className="k-loading-color" />
                         </div>
                    )}
                    <Grid data={rows} className="sba-area-data-grid">
                         <GridColumn
                              field="name"
                              title="Job Title"
                              width="360px"
                              cells={{ data: cells.name }}
                         />
                         <GridColumn
                              field="active"
                              title="Active"
                              width="120px"
                              cells={{ data: cells.active }}
                         />
                         <GridColumn
                              title=" "
                              width="120px"
                              cells={{ data: cells.save }}
                         />
                         <GridColumn
                              title=" "
                              width="80px"
                              cells={{ data: cells.remove }}
                         />
                    </Grid>
               </div>
          </div>
     );
};

export default RetailJobTitlesGrid;
